import { type FieldsResponse, flattenStreams } from 'api-validations/pangea'

import type { LocalizationContext } from '../localization'
import { translateCropKey } from './cropLabel'
import type { Stream, UnlabeledStream } from './useAllStreams'

type Translate = LocalizationContext['t']

type LabeledCatalog = {
  streams: Stream[]
  passCounts: ReadonlyMap<string, number>
}

const ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'

/**
 * Converts a number to a base-26 representation using letters A-Z.
 * For example, 0 -> A, 1 -> B, ..., 25 -> Z, 26 -> AA, 27 -> AB, etc.
 */
const toBaseAlpha = (num: number): string => {
  let result = ''
  while (num >= 0) {
    result = ALPHABET[num % 26] + result
    num = Math.floor(num / 26) - 1
  }
  return result
}

const createLabeler = ({ t, passCounts }: { t: Translate; passCounts: Map<string, number> }) => {
  const cropLabels = new Map<string, string>()

  const cropLabel = (cropKey: string) => {
    const cached = cropLabels.get(cropKey)
    if (cached !== undefined) return cached
    const label = translateCropKey({ cropKey, t })
    cropLabels.set(cropKey, label)
    return label
  }

  const passName = (stream: UnlabeledStream) => {
    if (stream.passName !== undefined && stream.passName !== '') return stream.passName
    /**
     * We're trying to count how many passes of a given task occur in a field during
     * one crop cycle. So three spray passes are Pass A, B, and C.
     */
    const passKey =
      stream.client + stream.farm + stream.field + stream.task + stream.cropKey + stream.season
    const passCount = passCounts.get(passKey) ?? 0
    passCounts.set(passKey, passCount + 1)
    return t(($) => $.map.passIndex, { passIndex: toBaseAlpha(passCount) })
  }

  return (stream: UnlabeledStream): Stream => ({
    ...stream,
    cropLabel: cropLabel(stream.cropKey),
    passName: passName(stream)
  })
}

const labelCatalog = ({ data, t }: { data: FieldsResponse; t: Translate }): LabeledCatalog => {
  const passCounts = new Map<string, number>()
  const label = createLabeler({ t, passCounts })
  return { streams: flattenStreams(data).map(label), passCounts }
}

/**
 * If syncd hasn't yet written a shard for a stream, the stream is not in the
 * static stream list, but Emissary still knows about it.
 */
export const liveOnlyStreams = ({
  catalog,
  liveStreams
}: {
  catalog: readonly Pick<UnlabeledStream, 'streamId'>[]
  liveStreams: ReadonlyMap<string, UnlabeledStream>
}): UnlabeledStream[] => {
  const catalogIds = new Set(catalog.map((stream) => stream.streamId))
  return Array.from(liveStreams.values()).filter((stream) => !catalogIds.has(stream.streamId))
}

const withLiveStreams = ({
  catalog,
  liveStreams,
  t
}: {
  catalog: LabeledCatalog
  liveStreams: ReadonlyMap<string, UnlabeledStream>
  t: Translate
}): Stream[] => {
  const streams = catalog.streams.map((stream) =>
    liveStreams.has(stream.streamId) ? { ...stream, isLive: true } : stream
  )

  const label = createLabeler({ t, passCounts: new Map(catalog.passCounts) })
  const liveOnly = liveOnlyStreams({ catalog: catalog.streams, liveStreams }).map(label)

  return [...streams, ...liveOnly]
}

let labeled: { data: FieldsResponse; t: Translate; catalog: LabeledCatalog } | undefined
let merged:
  | {
      catalog: LabeledCatalog
      liveStreams: ReadonlyMap<string, UnlabeledStream>
      streams: Stream[]
    }
  | undefined

export const toAllStreams = ({
  data,
  liveStreams,
  t
}: {
  data: FieldsResponse
  liveStreams: ReadonlyMap<string, UnlabeledStream>
  t: Translate
}): Stream[] => {
  if (labeled?.data !== data || labeled.t !== t) {
    labeled = { data, t, catalog: labelCatalog({ data, t }) }
  }

  if (merged?.catalog !== labeled.catalog || merged.liveStreams !== liveStreams) {
    merged = {
      catalog: labeled.catalog,
      liveStreams,
      streams: withLiveStreams({ catalog: labeled.catalog, liveStreams, t })
    }
  }

  return merged.streams
}
